<template>
  <div class="table-responsive-overflow">
    <table class="sc3-table data-table" aria-label="Data Table">
      <colgroup>
        <col
          v-for="column in columns"
          :key="column.id"
          :style="{
            width: typeof column.width === 'number' ? `${column.width}px`: undefined
          }"
        />
      </colgroup>
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.id"
            @click="column.keyForSorting && sortBy(column.keyForSorting)"
            :class="{
              'sortable': column.keyForSorting
            }"
            scope="col"
          >
            <span class="header-name-container">
              <template v-if="column.title">{{ column.title }}</template>
              <i
                v-if="column.keyForSorting"
                class="fas"
                :class="{
                  'fa-sort': column.keyForSorting !== currentKeyForSorting,
                  'fa-sort-amount-down': isDescending && column.keyForSorting === currentKeyForSorting,
                  'fa-sort-amount-up': !isDescending && column.keyForSorting === currentKeyForSorting
                }"
              ></i>
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in sortedRows">
          <td
            v-for="column in columns"
            :class="column.getTdClass ? column.getTdClass(row) : ''"
          >
            <button
              v-if="column.onClick && (column.getIconClass || column.keyForRendering)"
              @click="column.onClick(row)"
              class="clickable-row-text"
              type="button"
            >
              <template v-if="column.getIconClass">
                <i :class="column.getIconClass(row)"></i>
              </template>
              <span v-else-if="column.keyForRendering" v-sanitize-html="row[column.keyForRendering]"></span>
            </button>
            <span v-else-if="column.keyForRendering" v-sanitize-html="row[column.keyForRendering]"></span>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script lang="ts" generic="T extends object" setup>
  import { computed, onMounted, ref, toRefs } from 'vue';
  import { sortObjectsClosure } from '@/utils/sortObjectsClosure';
  import { DataTableColumn } from '@/interfaces/DataTable';

  const props = defineProps<{
    rows: readonly T[];
    columns: DataTableColumn<T>[];
  }>();

  const isDescending = ref(true);
  const currentKeyForSorting = ref<keyof T>();
  const { rows, columns } = toRefs(props);

  const sortedRows = computed(() => {
    if (!currentKeyForSorting.value) {
      return rows.value;
    }

    const sortColumn = columns.value.find(column => column.keyForSorting === currentKeyForSorting.value);

    if (!sortColumn) {
      return rows.value;
    }

    const rowsCopy = [ ...rows.value ];

    rowsCopy.sort(sortObjectsClosure({
      propertyName: currentKeyForSorting.value,
      order: isDescending.value ? 'descending' : 'ascending',
      map: sortColumn.isNumeric ? Number : undefined
    }));

    return rowsCopy;
  });

  function sortBy(newKeyForSorting: keyof T, direction?: 'ascending' | 'descending') {
    const sortColumn = columns.value.find(column => column.keyForSorting === newKeyForSorting);

    if (!sortColumn) {
      return;
    }

    if (direction) {
      isDescending.value = direction === 'descending';
    } else if (newKeyForSorting !== currentKeyForSorting.value && sortColumn.initialSortDirection) {
      isDescending.value = sortColumn.initialSortDirection === 'descending';
    } else if (newKeyForSorting !== currentKeyForSorting.value) {
      isDescending.value = true;
    } else {
      isDescending.value = !isDescending.value;
    }

    currentKeyForSorting.value = newKeyForSorting;
  }

  onMounted(() => {
    for (const column of columns.value) {
      if (column.keyForSorting && column.sortOnInit) {
        sortBy(column.keyForSorting, column.sortOnInit);
        break;
      }
    }
  });
</script>

<style scoped>
  table.data-table {
    width: calc(100% - 2px);
  }

  .data-table tr:last-of-type td {
    border-bottom: 1px solid #c0ced8 !important;
  }

  .data-table td:last-of-type, th:last-of-type {
    border-right: 1px solid #c0ced8 !important;
  }

  .data-table th.select-all-cell {
    padding: 0px !important;
    vertical-align: middle;
  }

  .data-table button.select-all-button {
    all: unset;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  }

  .data-table .select-all-button:hover {
    cursor: pointer;
  }

  table.sc3-table.sc3-table-centered thead tr th,
  table.sc3-table.sc3-table-centered tbody tr td {
    text-align: center;
  }

  .data-table button.clickable-row-text {
    color: #0f4d8a;
    font-size: 14px;
    font-weight: 700;
    background: none;
    border: none;
  }

  table.sc3-table tbody tr:nth-of-type(odd) td {
    background-color: #f2f5f7;
  }

  table.sc3-table tbody tr:hover td {
    background-color: #dfe7ec;
  }

  table.sc3-table tbody tr td.bold-font {
    font-weight: 900;
  }

  table.sc3-table tbody tr td.light-font {
    font-weight: 400;
  }

  table.sc3-table tbody tr td.small-light-font {
    font-weight: 400;
    color: #707f8f;
    font-size: 12px;
  }

  table.sc3-table tbody tr td.green-font,
  table.sc3-table tbody tr td span.green-font {
    color: #1e824c;
  }

  table.sc3-table tbody tr td.red-font,
  table.sc3-table tbody tr td span.red-font {
    color: #cc000e;
  }

  table.sc3-table tbody tr td.light-green-background-color,
  table.sc3-table tbody tr td span.light-green-background-color {
    background-color: #eafaf2;
  }

  table.sc3-table tbody tr td.grey-background-color,
  table.sc3-table tbody tr td span.grey-background-color {
    background-color: #d2dbe2;
  }

  table.sc3-table tbody tr td.white-background-color,
  table.sc3-table tbody tr td span.white-background-color {
    background-color: white;
  }

  table.sc3-table tbody tr td.row-select-checkbox label, table.sc3-table tbody tr th {
    cursor: pointer;
  }

  .data-table th.sortable {
    cursor: pointer;
  }

  .header-name-container {
    display: flex;
    gap: 5px;
    align-items: center;
    justify-content: space-between;
  }

  .header-name-container i {
    width: 14px;
  }

  .data-table th.sortable .fa-sort {
    opacity: 0.5;
  }

  .data-table tr > th:not(.row-select-checkbox),
  .data-table tr > td:not(.row-select-checkbox) {
    flex: 1;
  }
</style>

<style>
  .table-responsive-overflow {
    overflow-x: auto !important;
    overflow-y: visible !important;
  }

  .screen-size-tablet .table-responsive-overflow,
  .screen-size-small-tablet .table-responsive-overflow,
  .screen-size-phone .table-responsive-overflow {
    width: 100%;
  }
</style>
